import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { pillarApi, pillarErr } from "@/lib/pillarApi";
import { AIReview } from "@/components/nds/windows/pillar/AIReview";

const MIN = 250;
const days = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

function Row({ p, a, residentId, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const edited = useRef(false);
  const onDraft = useCallback((t, force) => { if (force || !edited.current) { setText(t); edited.current = false; } }, []);
  const run = async (fn) => { setBusy(true); setErr(null); try { await fn(); onDone(); } catch (e) { setErr(pillarErr(e)); } finally { setBusy(false); } };
  const assign = () => run(() => pillarApi.post(`/residents/${residentId}/pillar-assignments`, { pillar_id: p.id }));
  const verify = () => run(() => pillarApi.post(`/pillar-assignments/${a.id}/staff-response`, { response: text }));
  const age = days(a?.staff_response_at);
  let state; let action = null;
  if (!a) { state = "Never assigned."; action = "assign"; }
  else if (a.status === "resident_submitted") { state = `Week ${a.program_week} answered ${days(a.submitted_at) ?? 0}d ago · needs your verification.`; action = "verify"; }
  else if (a.status === "staff_assessed") { state = `Week ${a.program_week} verified ${age}d ago.`; action = age > 14 ? "assign" : null; }
  else { state = `Week ${a.program_week} assigned ${days(a.assigned_at) ?? 0}d ago · waiting for the resident to answer in their portal.`; }
  return (
    <li className="py-3 space-y-2" data-testid={`pfix-${p.id}`}>
      <div className="flex items-baseline gap-2"><span className="font-medium text-nex-silver">{p.name}</span>
        <span className={`text-xs ${action ? "text-red-200/90" : "text-nex-muted"}`} data-testid={`pfix-state-${p.id}`}>{state}</span></div>
      {action === "assign" && <>
        <p className="text-xs text-nex-chrome">{a ? "Over 14 days since it was verified. " : ""}Step 1: assign the next week's question. The resident answers it in their portal, then it comes back here for you to verify.</p>
        <button type="button" disabled={busy} onClick={assign} data-testid={`pfix-assign-${p.id}`} className="px-3 py-1.5 bg-nex-gold text-black text-xs font-semibold rounded-sm disabled:opacity-50">
          {busy ? "Assigning…" : `Assign next week · ${p.name}`}</button>
      </>}
      {action === "verify" && <>
        <div className="text-xs bg-white/5 border border-white/10 p-2 rounded-sm" data-testid={`pfix-answer-${p.id}`}>
          <div className="text-nex-muted">Resident's answer: <span className="text-nex-silver">{typeof a.selected_option === "object" && a.selected_option ? `${a.selected_option.letter ? `${a.selected_option.letter}. ` : ""}${a.selected_option.text || ""}` : a.selected_option || "—"}</span></div>
          {a.resident_reason && <p className="mt-1 text-nex-chrome whitespace-pre-wrap">{a.resident_reason}</p>}
        </div>
        <AIReview assignment={a} onDraft={onDraft} />
        <textarea value={text} onChange={(e) => { edited.current = true; setText(e.target.value); }} rows={text.length > 300 ? 10 : 4} data-testid={`pfix-response-${p.id}`} placeholder="The AI draft appears here in the style you pick; edit it before you verify…"
          className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-sm text-white rounded-sm" />
        <div className="flex items-center gap-2">
          <button type="button" disabled={busy || text.trim().length < MIN} onClick={verify} data-testid={`pfix-verify-${p.id}`} className="px-3 py-1.5 bg-nex-gold text-black text-xs font-semibold rounded-sm disabled:opacity-40">
            {busy ? "Verifying…" : "Verify pillar"}</button>
          <span className="text-[11px] text-nex-muted" data-testid={`pfix-count-${p.id}`}>{text.trim().length}/{MIN} characters minimum</span>
        </div>
      </>}
      {err && <p className="text-xs text-red-300" data-testid={`pfix-error-${p.id}`}>{err}</p>}
    </li>
  );
}

/** Clear next step per pillar: assign the next week, wait for the resident, or verify their answer. */
const STALE_MS = 14 * 864e5;
const SHOW = {
  pillar_verification: (a) => a?.status === "resident_submitted",
  pillar_assignment: (a) => !a || (a.status === "staff_assessed" && Date.now() - new Date(a.staff_response_at || 0) > STALE_MS),
};

export default function PillarFixPanel({ residentId, only, mode, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => Promise.all([pillarApi.get("/pillars"), pillarApi.get("/pillar-assignments", { resident_id: residentId })])
    .then(([p, a]) => setD({ pillars: p.pillars, latest: Object.fromEntries(p.pillars.map((x) => [x.id, a.assignments.find((y) => y.pillar_id === x.id)])) }))
    .catch((e) => setErr(pillarErr(e)));
  useEffect(() => { load(); }, [residentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <p className="text-xs text-red-300">{err}</p>;
  if (!d) return <Loader2 className="h-4 w-4 animate-spin" />;
  const rows = d.pillars.filter((p) => (!only || p.id === only) && (!SHOW[mode] || SHOW[mode](d.latest[p.id])));
  return (
    <ul className="divide-y divide-white/10" data-testid="pillar-fix-panel">
      {!rows.length && <li className="py-3 text-xs text-slate-400" data-testid="pfix-none">Nothing left to do here.</li>}
      {rows.map((p) => <Row key={p.id} p={p} a={d.latest[p.id]} residentId={residentId} onDone={() => { load(); onChanged?.(); }} />)}
    </ul>
  );
}
